// M2 field definitions (Gusher & Rotary era, 1901-1945). Same FieldDef shape as src/data/fields.js
// (DESIGN §9.2 + the data-agent extensions), plus the M2 keys below. Merged into FIELDS by
// src/data/fields.js, but hidden (non-enumerable) until enableM2Fields() is called — see M2_SPEC.md §0.
//
// M2 FieldDef extensions (all optional; the M1 sim ignores them):
//   discoveryDay      day of the famous discovery (news); opensDay may be earlier so the player can lease first
//   townLot           { plotAcres } tiny town-lot plots: many wells, fierce offset drainage
//   prorationGroup    'east_texas' | 'texas' | 'oklahoma' | 'yates' | 'california' | null  (see m2/rules.js)
//   recommendedRig    'rotary' | 'cable' — hint for the UI
//   flareGas          true: associated gas is flared (fire/H2S exposure, conservation fines from flag 'flaringLimits')
// Strata extensions:  gas (gas-charged, kicks/blows), heaving (unconsolidated sand: cable holes cave at once;
//   rotary with mud >= 8.8 ppg holds it), balling (gumbo: bit balling at high WOB/RPM with thin mud),
//   lossZone (cavernous/fractured: low frac, lost circulation), salt (washes out with fresh water mud)
// Reservoir extensions: rivalDrain [[year, fraction of remaining drained per year by other operators]],
//   gasCap (gas cap: fire risk while gushing, GOR blowouts), waterInvasion { atRemaining, rampDays }
//   (Golden Lane: wells suddenly "go to salt water"), waterDrive (strong aquifer: slow pressure decline),
//   condensate (naphtha/wet gas liquids)
//
// Depth fit rule (tests): a reservoir's host stratum must contain top + dip*min(maxRadius,4) + thick, and
// non-reservoir strata stay at <= 8.8 ppg pore unless they are meant to kick (gas: true or deep basement).
import { dateToDay } from '../../core/time.js';

// Same builder as src/data/fields.js; extra keys (incl. an explicit frac) override the defaults.
const FRAC_CAP_PPG = 19; // M2_SPEC §18.5 (same cap as src/data/fields.js)
function stack(layers) {
  let top = 0;
  return layers.map(([bottom, rock, name, hard, pore, stableDays, extra = {}]) => {
    const mid = (top + bottom) / 2;
    const s = { top, bottom, rock, name, hard, pore, frac: +Math.min(FRAC_CAP_PPG, 12 + 1.6 * mid / 1000 + (hard >= 5 ? 0.6 : 0)).toFixed(2), stableDays, h2s: 0, ...extra };
    top = bottom;
    return s;
  });
}

const RES = { di: 0.5, b: 0.5, api: 35, sour: false, rf: 0.18, h2s: 0 };
const D = (y, m, d) => dateToDay(y, m, d);
const WATER = { water: true };
const HEAVE = { water: true, heaving: true };
const GUMBO = { balling: true };

export const M2_FIELD_DEFS = [
  // ======================================================================== Spindletop, TX
  {
    id: 'spindletop', name: 'Spindletop, Beaumont TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1901, 1, 1), discoveryDay: D(1901, 1, 10), grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 30.02, lon: -94.07, startPlot: 19, townLot: { plotAcres: 0.5 }, prorationGroup: 'texas', recommendedRig: 'rotary',
    leaseBase: 1500, leaseYear: 1901,
    // Doormat lots on the Hogg-Swayne tract went for fortunes in spring 1901; the "Second Spindletop" flank boom in 1925-27.
    boomCurve: [[1901.0, 1], [1901.05, 4], [1901.25, 20], [1901.5, 25], [1901.75, 12], [1902.0, 6], [1902.5, 2.5], [1903.5, 0.8], [1910, 0.3],
                [1925.5, 0.4], [1926.0, 2], [1927.5, 1], [1935, 0.4]],
    sour: { mult: 0.8, flag: 'gulfRefineries', multAfter: 0.92 },
    transport: ['teamster', 'rail@1901', 'gathering_pipe@1901', 'coastal_tanker@1902', 'trunk_pipeline@1902', 'tank_truck@1915'],
    blurb: 'A low hill of sour gas seeps outside Beaumont. Captain Lucas and the Hamill brothers think there is oil under the salt. They are right, spectacularly.',
    strata: stack([
      [30,   'clay',      'Beaumont clay',                 2, 8.5, 10],
      [160,  'sand',      'Heaving quicksand',             1, 8.8, 1, HEAVE],
      [420,  'clay',      'Gumbo clay',                    2, 8.7, 6, GUMBO],
      [470,  'sand',      'Shallow gas sand',              2, 9.4, 3, { gas: true, h2s: 200 }],
      [700,  'clay',      'Gumbo & shale',                 2, 8.8, 8, GUMBO],
      [760,  'anhydrite', 'Gypsum & anhydrite cap',        5, 8.8, 200, { h2s: 80 }],
      [1250, 'limestone', 'Cavernous cap-rock limestone',  5, 8.8, 150, { h2s: 150, frac: 11.2, lossZone: true }],
      [2300, 'shale',     'Anahuac shale',                 3, 8.8, 30],
      [3600, 'sand',      'Frio flank sands',              3, 8.8, 15],
      [6000, 'salt',      'Salt core',                     3, 9.6, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 3.5, cy: 2, rx: 1.8, ry: 1.4, reservoirs: [
        { id: 'sp_caprock', name: 'Cap-rock pay (Lucas)', top: 1020, dipFtPerPlot: 70, thick: 50, ooip: 160e6, qiRange: [4000, 75000], pore: 10.4, gusherChance: 0.9,
          ...RES, rf: 0.25, di: 2.2, b: 0.3, api: 22, sour: true, h2s: 150, gasCap: true,
          rivalDrain: [[1901.05, 0], [1901.3, 0.7], [1902, 1.1], [1903.5, 0.8], [1905, 0.3], [1910, 0.1]] },
      ] },
      { cx: 7, cy: 1.5, rx: 1.3, ry: 1.6, reservoirs: [
        { id: 'sp_flank_east', name: 'East flank sands (Yount-Lee)', top: 2500, dipFtPerPlot: 150, thick: 80, ooip: 40e6, qiRange: [300, 5000], pore: 9.8, gusherChance: 0.35,
          ...RES, rf: 0.2, di: 0.9, api: 26, sour: true, h2s: 40, rivalDrain: [[1925.8, 0], [1926.3, 0.6], [1928, 0.4], [1932, 0.15]] },
      ] },
      { cx: 0.5, cy: 3.8, rx: 1.5, ry: 1.2, reservoirs: [
        { id: 'sp_flank_west', name: 'Southwest flank sands', top: 2900, dipFtPerPlot: 160, thick: 70, ooip: 30e6, qiRange: [300, 4000], pore: 9.8, gusherChance: 0.3,
          ...RES, rf: 0.2, di: 0.9, api: 26, sour: true, h2s: 40, rivalDrain: [[1925.8, 0], [1926.5, 0.5], [1929, 0.3], [1932, 0.15]] },
      ] },
    ],
    seeps: [19, 20, 12],
    hazards: { lightning: 0.0003, fire: 0.0015 },
  },

  // ======================================================================== Sour Lake, TX
  {
    id: 'sour_lake', name: 'Sour Lake, TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1902, 9, 1), discoveryDay: D(1903, 1, 15), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 30.14, lon: -94.41, prorationGroup: 'texas', recommendedRig: 'rotary',
    leaseBase: 600, leaseYear: 1902,
    boomCurve: [[1902.67, 1], [1903.0, 5], [1903.4, 8], [1904, 3], [1905, 1], [1910, 0.5]],
    sour: { mult: 0.75, flag: 'gulfRefineries', multAfter: 0.9 },
    transport: ['teamster', 'rail@1902', 'gathering_pipe@1903', 'trunk_pipeline@1903', 'tank_truck@1915'],
    blurb: 'A spa town of sulphur springs in the Big Thicket. The Texas Company makes its name here, if the stink does not knock you down first.',
    strata: stack([
      [25,   'clay',      'Swamp clay',                    2, 8.5, 8],
      [120,  'sand',      'Heaving sand',                  1, 8.8, 1, HEAVE],
      [520,  'clay',      'Gumbo clay',                    2, 8.7, 6, GUMBO],
      [600,  'anhydrite', 'Sulphur cap rock',              5, 8.8, 150, { h2s: 300 }],
      [1000, 'limestone', 'Porous cap rock',               5, 8.8, 120, { h2s: 400, frac: 11.4, lossZone: true }],
      [2400, 'shale',     'Flank shale & sand',            3, 8.8, 30],
      [6000, 'salt',      'Salt core',                     3, 9.6, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 1.6, ry: 1.2, reservoirs: [
        { id: 'sl_cap', name: 'Sour Lake cap rock', top: 650, dipFtPerPlot: 60, thick: 60, ooip: 50e6, qiRange: [500, 15000], pore: 10.0, gusherChance: 0.6,
          ...RES, rf: 0.2, di: 1.6, b: 0.4, api: 20, sour: true, h2s: 400, rivalDrain: [[1902.9, 0], [1903.2, 0.8], [1904.5, 0.6], [1907, 0.2]] },
      ] },
    ],
    seeps: [8, 9, 14],
    hazards: { lightning: 0.0003, fire: 0.001, subsidence: 0.0001 },
  },

  // ======================================================================== Batson, TX
  {
    id: 'batson', name: 'Batson Prairie, TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1903, 10, 31), discoveryDay: D(1903, 10, 31), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 30.24, lon: -94.61, prorationGroup: 'texas', recommendedRig: 'rotary',
    leaseBase: 500, leaseYear: 1903,
    boomCurve: [[1903.83, 1], [1904.0, 6], [1904.3, 7], [1905, 2], [1906, 0.6]],
    sour: { mult: 0.8, flag: 'gulfRefineries', multAfter: 0.92 },
    transport: ['teamster', 'gathering_pipe@1903', 'rail@1904', 'trunk_pipeline@1904', 'tank_truck@1915'],
    blurb: 'Ten thousand men in a mud-hole prairie town with no law to speak of. Shallow flank sands come in big and quit fast.',
    strata: stack([
      [20,   'clay',      'Prairie clay',                  2, 8.5, 8],
      [140,  'sand',      'Heaving sand',                  1, 8.8, 1, HEAVE],
      [700,  'clay',      'Gumbo',                         2, 8.7, 6, GUMBO],
      [760,  'anhydrite', 'Cap rock',                      5, 8.8, 150, { h2s: 100 }],
      [1300, 'sand',      'Batson flank sands',            3, 8.8, 15],
      [6000, 'salt',      'Salt core',                     3, 9.6, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 3, cy: 2, rx: 2.0, ry: 1.4, reservoirs: [
        { id: 'bt_sands', name: 'Batson flank sands', top: 800, dipFtPerPlot: 50, thick: 60, ooip: 30e6, qiRange: [300, 8000], pore: 9.8, gusherChance: 0.5,
          ...RES, rf: 0.2, di: 1.8, b: 0.4, api: 24, sour: true, h2s: 60, rivalDrain: [[1903.85, 0], [1904.1, 1.0], [1905, 0.6], [1907, 0.2]] },
      ] },
    ],
    seeps: [14],
    hazards: { lightning: 0.0003, fire: 0.0012 },
  },

  // ======================================================================== Humble, TX
  {
    id: 'humble', name: 'Humble (Moonshine Hill), TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1905, 1, 7), discoveryDay: D(1905, 1, 7), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 29.99, lon: -95.26, prorationGroup: 'texas', recommendedRig: 'rotary',
    leaseBase: 500, leaseYear: 1905,
    boomCurve: [[1905.0, 1], [1905.2, 6], [1905.6, 5], [1906.5, 1.5], [1908, 0.6], [1914, 1.2], [1916, 0.6]],
    sour: { mult: 0.85, flag: 'gulfRefineries', multAfter: 0.95 },
    transport: ['teamster', 'rail@1905', 'gathering_pipe@1905', 'trunk_pipeline@1905', 'tank_truck@1915'],
    blurb: 'Pine woods north of Houston. A shallow cap-rock boom in 1905, then deep flank sands for anyone with a rig that can reach them.',
    strata: stack([
      [30,   'clay',      'Coastal clay',                  2, 8.5, 8],
      [180,  'sand',      'Heaving sand',                  1, 8.8, 1, HEAVE],
      [800,  'clay',      'Gumbo',                         2, 8.7, 6, GUMBO],
      [880,  'anhydrite', 'Cap rock',                      5, 8.8, 150, { h2s: 60 }],
      [1500, 'sand',      'Shallow flank sands',           3, 8.8, 15],
      [2400, 'shale',     'Shale breaks',                  3, 8.8, 30],
      [3600, 'sand',      'Deep Humble sands',             3, 8.8, 15],
      [6000, 'salt',      'Salt core',                     3, 9.6, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 1.8, ry: 1.3, reservoirs: [
        { id: 'hu_shallow', name: 'Shallow flank sands', top: 950, dipFtPerPlot: 50, thick: 70, ooip: 40e6, qiRange: [300, 8000], pore: 9.8, gusherChance: 0.45,
          ...RES, rf: 0.2, di: 1.4, b: 0.4, api: 22, sour: true, h2s: 40, rivalDrain: [[1905.05, 0], [1905.4, 0.9], [1907, 0.5], [1910, 0.15]] },
        { id: 'hu_deep', name: 'Deep Humble sands', top: 2700, dipFtPerPlot: 90, thick: 80, ooip: 25e6, qiRange: [200, 2500], pore: 9.6, gusherChance: 0.2,
          ...RES, rf: 0.2, di: 0.6, api: 28, h2s: 20, rivalDrain: [[1914, 0], [1914.5, 0.4], [1918, 0.2]] },
      ] },
    ],
    seeps: [9],
    hazards: { lightning: 0.0003, fire: 0.001 },
  },

  // ======================================================================== Glenn Pool, OK
  {
    id: 'glenn_pool', name: 'Glenn Pool, Indian Territory', body: 'earth', region: 'oklahoma', regionId: 'oklahoma',
    era: 'gusher', opensDay: D(1905, 11, 22), discoveryDay: D(1905, 11, 22), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 35.95, lon: -96.03, prorationGroup: 'oklahoma', recommendedRig: 'cable',
    leaseBase: 250, leaseYear: 1905,
    boomCurve: [[1905.9, 1], [1906.3, 3], [1907, 4], [1908, 2.5], [1912, 1.2], [1920, 0.8]],
    transport: ['teamster', 'rail@1905', 'gathering_pipe@1906', 'trunk_pipeline@1907', 'tank_truck@1915'],
    blurb: 'Creek Nation allotments on a cow pasture south of Tulsa. Sweet light crude in a wide sheet of sand, under limestone a fishtail cannot touch.',
    strata: stack([
      [30,   'clay',      'Prairie soil & clay',           2, 8.5, 10],
      [300,  'shale',     'Coffeyville shale',             3, 8.6, 60],
      [340,  'sand',      'Fresh water sand',              4, 8.9, 20, WATER],
      [700,  'shale',     'Nowata shale',                  3, 8.7, 60],
      [780,  'limestone', 'Oswego lime',                   6, 8.7, 200],
      [1350, 'shale',     'Cherokee shale',                3, 8.8, 60],
      [1600, 'sand',      'Glenn (Bartlesville) sand',     5, 8.8, 60],
      [2400, 'limestone', 'Mississippi lime',              6, 9.0, 200],
      [6000, 'dolomite',  'Arbuckle dolomite',             7, 9.0, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 3.2, ry: 2.2, reservoirs: [
        { id: 'gp_glenn', name: 'Glenn sand', top: 1430, dipFtPerPlot: 18, thick: 70, ooip: 60e6, qiRange: [100, 2500], pore: 9.4, gusherChance: 0.12,
          ...RES, rf: 0.2, di: 0.45, api: 38, rivalDrain: [[1906, 0], [1906.5, 0.12], [1910, 0.1], [1920, 0.06]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0002, tornado: 0.0001 },
  },

  // ======================================================================== Cushing, OK
  {
    id: 'cushing', name: 'Cushing (Drumright), OK', body: 'earth', region: 'oklahoma', regionId: 'oklahoma',
    era: 'gusher', opensDay: D(1912, 3, 17), discoveryDay: D(1912, 3, 17), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 35.98, lon: -96.6, prorationGroup: 'oklahoma', recommendedRig: 'cable',
    leaseBase: 400, leaseYear: 1912,
    boomCurve: [[1912.2, 1], [1912.6, 3], [1913.5, 6], [1914.5, 8], [1915.3, 7], [1916, 4], [1918, 1.5], [1922, 0.6]],
    transport: ['teamster', 'rail@1912', 'gathering_pipe@1912', 'trunk_pipeline@1913', 'tank_truck@1915'],
    blurb: 'Tom Slick’s Wheeler No. 1 opened the field of the decade. By 1915 one barrel in six in America comes from here, and the gas wells roar.',
    strata: stack([
      [30,   'clay',      'Red clay',                      2, 8.5, 10],
      [400,  'shale',     'Vamoosa shale & sand',          3, 8.6, 60],
      [430,  'sand',      'Fresh water sand',              4, 8.9, 20, WATER],
      [1300, 'shale',     'Pawhuska shale & lime',         4, 8.7, 70],
      [1500, 'sand',      'Layton & Jones sands',          4, 8.8, 40],
      [2250, 'limestone', 'Big Lime & Oswego',             6, 8.8, 200],
      [2700, 'sand',      'Bartlesville sand',             5, 8.8, 50],
      [3300, 'limestone', 'Mississippi chat & lime',       6, 9.0, 200],
      [6000, 'dolomite',  'Wilcox & Arbuckle',             7, 9.1, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.6, reservoirs: [
        { id: 'cu_layton', name: 'Layton sand', top: 1320, dipFtPerPlot: 25, thick: 50, ooip: 15e6, qiRange: [50, 800], pore: 9.2, gusherChance: 0.05,
          ...RES, di: 0.6, api: 38 },
        { id: 'cu_bartlesville', name: 'Bartlesville sand', top: 2300, dipFtPerPlot: 40, thick: 120, ooip: 90e6, qiRange: [300, 6000], pore: 10.2, gusherChance: 0.35,
          ...RES, di: 0.9, api: 40, gasCap: true, rivalDrain: [[1912.3, 0], [1914, 0.5], [1915.3, 0.9], [1917, 0.4], [1920, 0.15]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0008, tornado: 0.0001 },
  },

  // ======================================================================== Healdton, OK
  {
    id: 'healdton', name: 'Healdton, OK', body: 'earth', region: 'oklahoma', regionId: 'oklahoma',
    era: 'gusher', opensDay: D(1913, 8, 4), discoveryDay: D(1913, 8, 4), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 34.23, lon: -97.49, prorationGroup: 'oklahoma', recommendedRig: 'cable',
    leaseBase: 200, leaseYear: 1913,
    boomCurve: [[1913.6, 1], [1914, 3], [1914.5, 2], [1916, 1.3], [1920, 0.8]],
    // Heavy, sulphurous crude with no pipeline outlet: buyers pay little until Magnolia's line arrives.
    sour: { mult: 0.6, flag: 'healdtonOutlet', multAfter: 0.85 },
    transport: ['teamster', 'rail@1913', 'gathering_pipe@1914', 'trunk_pipeline@1915', 'tank_truck@1915'],
    blurb: 'Shallow, prolific and stinking. Oil sits in earthen pits for want of a pipeline, and the price shows it.',
    strata: stack([
      [20,   'clay',      'Red beds soil',                 2, 8.5, 10],
      [350,  'shale',     'Permian red beds',              3, 8.6, 60],
      [380,  'sand',      'Fresh water sand',              4, 8.9, 20, WATER],
      [850,  'shale',     'Hoxbar shale',                  3, 8.7, 60],
      [1300, 'sand',      'Healdton sands',                4, 8.8, 40],
      [3000, 'limestone', 'Arbuckle limestone',            7, 8.8, 200],
      [6000, 'dolomite',  'Arbuckle dolomite',             7, 9.1, 200],
    ]),
    prospects: [
      { cx: 3, cy: 1.5, rx: 2.5, ry: 1.6, reservoirs: [
        { id: 'he_sands', name: 'Healdton sands', top: 880, dipFtPerPlot: 30, thick: 120, ooip: 40e6, qiRange: [50, 1500], pore: 9.3, gusherChance: 0.1,
          ...RES, rf: 0.2, di: 0.5, api: 26, sour: true, h2s: 80, rivalDrain: [[1913.7, 0], [1914.3, 0.3], [1916, 0.15]] },
      ] },
    ],
    seeps: [9],
    hazards: { lightning: 0.0003, fire: 0.0004 },
  },

  // ======================================================================== Ranger, TX
  {
    id: 'ranger', name: 'Ranger, TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1917, 10, 17), discoveryDay: D(1917, 10, 17), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 32.47, lon: -98.68, prorationGroup: 'texas', recommendedRig: 'cable',
    leaseBase: 300, leaseYear: 1917,
    boomCurve: [[1917.8, 1], [1918.2, 4], [1919, 10], [1919.7, 7], [1920.5, 2], [1921.5, 0.5], [1925, 0.3]],
    transport: ['teamster', 'rail@1917', 'tank_truck@1918', 'gathering_pipe@1918', 'trunk_pipeline@1919'],
    blurb: 'The McCleskey well saved the coal company and started a stampede. Deep limestone pay in small, scattered pods; the mud on Main Street swallows mules.',
    strata: stack([
      [30,   'clay',      'Red mud',                       2, 8.5, 6],
      [600,  'shale',     'Strawn shale',                  3, 8.6, 60],
      [650,  'sand',      'Fresh water sand',              4, 8.9, 20, WATER],
      [2000, 'shale',     'Strawn & Smithwick shale',      4, 8.7, 60],
      [2400, 'limestone', 'Marble Falls lime',             6, 8.8, 200],
      [3300, 'shale',     'Smithwick shale',               4, 8.8, 60],
      [3700, 'limestone', 'Ranger pay lime (Bend)',        6, 8.8, 200],
      [6000, 'limestone', 'Ellenburger',                   7, 9.2, 200],
    ]),
    prospects: [
      { cx: 1.5, cy: 1, rx: 1.3, ry: 1.0, reservoirs: [
        { id: 'ra_bend_a', name: 'Bend lime (McCleskey pod)', top: 3420, dipFtPerPlot: 35, thick: 60, ooip: 6e6, qiRange: [300, 3000], pore: 9.9, gusherChance: 0.25,
          ...RES, di: 1.8, b: 0.4, api: 40, rivalDrain: [[1917.9, 0], [1918.5, 0.6], [1920, 0.8], [1922, 0.3]] },
      ] },
      { cx: 4, cy: 2.5, rx: 1.2, ry: 1.0, reservoirs: [
        { id: 'ra_bend_b', name: 'Bend lime (south pod)', top: 3450, dipFtPerPlot: 40, thick: 50, ooip: 4e6, qiRange: [200, 2500], pore: 9.8, gusherChance: 0.2,
          ...RES, di: 1.8, b: 0.4, api: 40, rivalDrain: [[1918.2, 0], [1918.8, 0.6], [1920, 0.8], [1922, 0.3]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0004 },
  },

  // ======================================================================== Burkburnett, TX
  {
    id: 'burkburnett', name: 'Burkburnett, TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1918, 7, 29), discoveryDay: D(1918, 7, 29), grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 34.1, lon: -98.57, townLot: { plotAcres: 0.25 }, prorationGroup: 'texas', recommendedRig: 'cable',
    leaseBase: 250, leaseYear: 1918,
    boomCurve: [[1918.55, 1], [1918.8, 8], [1919.3, 12], [1919.8, 6], [1920.5, 2], [1922, 0.6]],
    transport: ['teamster', 'rail@1918', 'gathering_pipe@1918', 'tank_truck@1918', 'trunk_pipeline@1919'],
    blurb: '“Fowler’s Folly” came in at the edge of town. Within weeks there are derricks in back yards, and stock salesmen on every corner.',
    strata: stack([
      [25,   'clay',      'Red River silt',                2, 8.5, 6],
      [180,  'sand',      'River sand',                    2, 8.8, 4, WATER],
      [900,  'shale',     'Permian red beds',              3, 8.6, 60],
      [1200, 'shale',     'Cisco shale',                   3, 8.7, 60],
      [1300, 'sand',      'Gun Barrel sand',               4, 8.8, 40],
      [1700, 'shale',     'Shale & lime breaks',           4, 8.8, 60],
      [2000, 'sand',      'Burkburnett deep sands',        4, 8.8, 40],
      [6000, 'limestone', 'Ellenburger',                   7, 9.2, 200],
    ]),
    prospects: [
      { cx: 2, cy: 2, rx: 2.4, ry: 1.8, reservoirs: [
        { id: 'bu_gunbarrel', name: 'Gun Barrel sand', top: 1210, dipFtPerPlot: 15, thick: 40, ooip: 4e6, qiRange: [50, 600], pore: 9.1, gusherChance: 0.05, ...RES, di: 1.4, api: 40 },
        { id: 'bu_deep', name: 'Fowler sand', top: 1730, dipFtPerPlot: 25, thick: 80, ooip: 15e6, qiRange: [300, 3000], pore: 9.8, gusherChance: 0.25,
          ...RES, di: 1.5, api: 41, rivalDrain: [[1918.6, 0], [1919, 0.8], [1920, 0.7], [1922, 0.2]] },
      ] },
      { cx: 6, cy: 1, rx: 1.8, ry: 1.2, reservoirs: [
        { id: 'bu_northwest', name: 'Northwest Extension sand', top: 1760, dipFtPerPlot: 25, thick: 60, ooip: 10e6, qiRange: [500, 4000], pore: 9.9, gusherChance: 0.3,
          ...RES, di: 1.6, api: 41, rivalDrain: [[1919.3, 0], [1919.6, 0.9], [1920.5, 0.6], [1922, 0.2]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0006 },
  },

  // ======================================================================== Signal Hill, CA
  {
    id: 'signal_hill', name: 'Signal Hill, Long Beach CA', body: 'earth', region: 'california', regionId: 'california',
    era: 'gusher', opensDay: D(1921, 5, 1), discoveryDay: D(1921, 6, 23), grid: { w: 10, h: 6 }, waterDepthFt: 0,
    lat: 33.8, lon: -118.17, townLot: { plotAcres: 0.1 }, prorationGroup: 'california', recommendedRig: 'rotary',
    leaseBase: 800, leaseYear: 1921, royalty: 0.25, // house-lot owners held out for a quarter or more
    boomCurve: [[1921.33, 1], [1921.5, 3], [1921.8, 10], [1922.5, 14], [1923.5, 10], [1924.5, 4], [1927, 1.5], [1935, 0.8]],
    transport: ['tank_truck@1921', 'gathering_pipe@1921', 'rail@1921', 'coastal_tanker@1921'],
    blurb: 'A hill of new bungalows over the Long Beach anticline. Shell’s Alamitos No. 1 turns it into a forest of derricks, one per house lot.',
    strata: stack([
      [40,   'sand',      'Terrace sand',                  1, 8.5, 3],
      [600,  'clay',      'Pleistocene clays',             2, 8.6, 15],
      [700,  'sand',      'Fresh water gravel',            2, 8.8, 4, WATER],
      [2400, 'shale',     'Pico siltstone & shale',        3, 8.8, 40],
      [3800, 'sand',      'Alamitos & Brown zones',        3, 8.8, 20],
      [4400, 'shale',     'Puente shale',                  4, 8.8, 40],
      [5600, 'sand',      'Union & deep sands',            4, 8.8, 25],
      [8000, 'shale',     'Puente schist & basement',      5, 10.0, 80],
    ]),
    prospects: [
      { cx: 4.5, cy: 2.5, rx: 4.0, ry: 2.2, reservoirs: [
        { id: 'sh_alamitos', name: 'Alamitos zone', top: 2950, dipFtPerPlot: 25, thick: 300, ooip: 150e6, qiRange: [300, 4000], pore: 9.7, gusherChance: 0.25,
          ...RES, rf: 0.2, di: 0.9, b: 0.6, api: 24, rivalDrain: [[1921.5, 0], [1922, 0.3], [1923.5, 0.45], [1926, 0.25], [1932, 0.1]] },
        { id: 'sh_union', name: 'Union & deep zones', top: 4500, dipFtPerPlot: 35, thick: 300, ooip: 60e6, qiRange: [500, 3000], pore: 10.2, gusherChance: 0.2,
          ...RES, rf: 0.2, di: 0.7, b: 0.6, api: 27, rivalDrain: [[1924, 0], [1925, 0.3], [1930, 0.15]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0008, earthquake: 0.00005 },
  },

  // ======================================================================== Santa Fe Springs, CA
  {
    id: 'santa_fe_springs', name: 'Santa Fe Springs, CA', body: 'earth', region: 'california', regionId: 'california',
    era: 'gusher', opensDay: D(1921, 10, 30), discoveryDay: D(1921, 10, 30), grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 33.94, lon: -118.08, prorationGroup: 'california', recommendedRig: 'rotary',
    leaseBase: 500, leaseYear: 1921, royalty: 0.2,
    boomCurve: [[1921.8, 1], [1922.5, 2], [1923.3, 8], [1923.8, 9], [1924.5, 3], [1927, 1]],
    transport: ['tank_truck@1921', 'gathering_pipe@1921', 'rail@1921', 'coastal_tanker@1922'],
    blurb: 'Union’s Bell No. 1 found it; the 1923 Meyer wells blew in so hard they set the sky on fire. Deep, hot and overpressured.',
    strata: stack([
      [40,   'sand',      'Alluvium',                      1, 8.5, 3],
      [800,  'clay',      'Pleistocene clays',             2, 8.6, 15],
      [900,  'sand',      'Fresh water gravel',            2, 8.8, 4, WATER],
      [3300, 'shale',     'Pico shale',                    3, 8.8, 40],
      [4900, 'sand',      'Meyer & Buckbee zones',         3, 8.8, 20],
      [6000, 'shale',     'Puente shale (gas-charged)',    4, 9.4, 40, { gas: true }],
      [7200, 'sand',      "O'Connell zone",                4, 8.8, 25],
      [9000, 'shale',     'Lower Puente',                  5, 11.0, 80],
    ]),
    prospects: [
      { cx: 3.5, cy: 2, rx: 2.6, ry: 1.7, reservoirs: [
        { id: 'sf_meyer', name: 'Meyer zone', top: 3700, dipFtPerPlot: 40, thick: 300, ooip: 120e6, qiRange: [1000, 15000], pore: 11.0, gusherChance: 0.5,
          ...RES, rf: 0.2, di: 1.2, api: 33, gasCap: true, rivalDrain: [[1922, 0], [1923.3, 0.8], [1924.5, 0.5], [1927, 0.2]] },
        { id: 'sf_oconnell', name: "O'Connell zone", top: 6200, dipFtPerPlot: 50, thick: 400, ooip: 50e6, qiRange: [500, 6000], pore: 12.0, gusherChance: 0.4,
          ...RES, rf: 0.2, di: 0.8, api: 34, rivalDrain: [[1926, 0], [1927, 0.4], [1932, 0.15]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0012, earthquake: 0.00005 },
  },

  // ======================================================================== Seminole, OK
  {
    id: 'seminole', name: 'Greater Seminole, OK', body: 'earth', region: 'oklahoma', regionId: 'oklahoma',
    era: 'gusher', opensDay: D(1926, 7, 16), discoveryDay: D(1926, 7, 16), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 35.23, lon: -96.68, prorationGroup: 'oklahoma', recommendedRig: 'rotary',
    leaseBase: 350, leaseYear: 1926,
    boomCurve: [[1926.5, 1], [1926.9, 5], [1927.4, 9], [1928, 5], [1929, 2], [1931.6, 0.5], [1935, 0.6]],
    transport: ['tank_truck@1926', 'gathering_pipe@1926', 'rail@1926', 'trunk_pipeline@1927'],
    blurb: 'Deep Wilcox sand under hard limestone, struck in pool after pool. By summer 1927 it floods the Mid-Continent with half a million barrels a day.',
    strata: stack([
      [30,   'clay',      'Red clay',                      2, 8.5, 10],
      [500,  'shale',     'Vamoosa sand & shale',          3, 8.6, 60],
      [540,  'sand',      'Fresh water sand',              4, 8.9, 20, WATER],
      [3300, 'shale',     'Pennsylvanian shale',           4, 8.8, 60],
      [3500, 'limestone', 'Hunton lime',                   6, 8.8, 200],
      [3900, 'limestone', 'Viola lime',                    7, 8.8, 200],
      [4300, 'sand',      'Wilcox sand (Simpson)',         5, 8.8, 60],
      [6000, 'dolomite',  'Arbuckle',                      7, 9.2, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.2, ry: 1.5, reservoirs: [
        { id: 'se_hunton', name: 'Hunton lime', top: 3320, dipFtPerPlot: 35, thick: 80, ooip: 20e6, qiRange: [100, 1200], pore: 9.5, gusherChance: 0.1, ...RES, di: 1.0, api: 36 },
        { id: 'se_wilcox', name: 'Wilcox sand', top: 3950, dipFtPerPlot: 40, thick: 120, ooip: 120e6, qiRange: [1000, 12000], pore: 10.4, gusherChance: 0.4,
          ...RES, di: 1.6, b: 0.4, api: 39, rivalDrain: [[1926.6, 0], [1927.2, 0.9], [1928.5, 0.6], [1931.6, 0.2]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0005, tornado: 0.0001 },
  },

  // ======================================================================== Yates, TX (Permian Basin)
  {
    id: 'yates', name: 'Yates, Pecos County TX', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1926, 10, 28), discoveryDay: D(1926, 10, 28), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 30.87, lon: -101.9, prorationGroup: 'yates', recommendedRig: 'cable',
    leaseBase: 150, leaseYear: 1926,
    boomCurve: [[1926.8, 1], [1927.3, 4], [1929, 6], [1930, 4], [1935, 2]],
    // Remote, sour, and no pipeline until Humble's line arrives.
    sour: { mult: 0.65, flag: 'permianOutlet', multAfter: 0.88 },
    transport: ['tank_truck@1926', 'gathering_pipe@1927', 'rail@1928', 'trunk_pipeline@1928'],
    blurb: 'Ira Yates’ ranch was too poor to graze goats. Under it lies cavernous dolomite that will flow more oil per well than anywhere in America, if anyone will buy it.',
    strata: stack([
      [20,   'sand',      'Caliche & sand',                3, 8.4, 20],
      [300,  'clay',      'Triassic red beds',             3, 8.6, 50],
      [340,  'sand',      'Santa Rosa water sand',         4, 8.9, 20, WATER],
      [800,  'anhydrite', 'Rustler & Salado salt',         4, 8.8, 100, { salt: true }],
      [1000, 'dolomite',  'Tansill & Yates dolomite',      6, 8.6, 200],
      [1700, 'dolomite',  'Cavernous San Andres dolomite', 6, 8.5, 250, { lossZone: true, frac: 9.6, h2s: 400 }],
      [6000, 'limestone', 'Clear Fork & deeper',           6, 9.0, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 3.0, ry: 2.2, reservoirs: [
        { id: 'ya_san_andres', name: 'San Andres dolomite', top: 1050, dipFtPerPlot: 15, thick: 300, ooip: 200e6, qiRange: [2000, 60000], pore: 9.2, gusherChance: 0.6,
          ...RES, rf: 0.3, di: 0.15, api: 30, sour: true, h2s: 600, waterDrive: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0003, heat: 0.0003 },
  },

  // ======================================================================== East Texas
  {
    id: 'east_texas', name: 'East Texas (Kilgore)', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'gusher', opensDay: D(1930, 9, 5), discoveryDay: D(1930, 10, 3), grid: { w: 10, h: 6 }, waterDepthFt: 0,
    lat: 32.38, lon: -94.87, startPlot: 22, prorationGroup: 'east_texas', recommendedRig: 'rotary',
    leaseBase: 100, leaseYear: 1930,
    boomCurve: [[1930.68, 1], [1930.8, 6], [1931.0, 12], [1931.4, 8], [1931.6, 3], [1931.75, 2], [1933, 2.5], [1936, 2], [1945, 1.5]],
    transport: ['teamster', 'tank_truck@1930', 'rail@1930', 'gathering_pipe@1930', 'trunk_pipeline@1931'],
    blurb: 'Dad Joiner’s wheezing rig on the Daisy Bradford farm. The geologists said no. Under five counties lies the biggest oil field anyone has ever found, and it is about to drown the market.',
    strata: stack([
      [40,   'sand',      'Sparta & Queen City sands',     2, 8.6, 6, WATER],
      [700,  'clay',      'Wilcox clay & lignite',         2, 8.6, 20],
      [800,  'sand',      'Carrizo-Wilcox water sand',     3, 8.9, 10, WATER],
      [2000, 'shale',     'Midway shale',                  3, 8.7, 50],
      [2600, 'clay',      'Navarro marl',                  3, 8.8, 40],
      [3050, 'limestone', 'Austin chalk',                  5, 8.8, 120],
      [3550, 'shale',     'Eagle Ford shale',              3, 8.8, 30],
      [3800, 'sand',      'Woodbine sand',                 4, 8.8, 40],
      [6000, 'limestone', 'Buda & Washita lime',           6, 9.1, 200],
    ]),
    prospects: [
      { cx: 5, cy: 2.5, rx: 4.2, ry: 3.0, reservoirs: [
        { id: 'et_woodbine', name: 'Woodbine sand', top: 3560, dipFtPerPlot: 8, thick: 120, ooip: 1.2e9, qiRange: [800, 15000], pore: 9.6, gusherChance: 0.1,
          ...RES, rf: 0.3, di: 0.08, b: 0.3, api: 39, waterDrive: true,
          rivalDrain: [[1930.8, 0], [1931.3, 0.08], [1931.6, 0.12], [1931.7, 0.03], [1935, 0.03]] },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0003 },
  },

  // ======================================================================== Golden Lane, Mexico
  {
    id: 'golden_lane', name: 'Golden Lane (Faja de Oro), Veracruz', body: 'earth', region: 'mexico', regionId: 'mexico',
    era: 'gusher', opensDay: D(1910, 9, 1), discoveryDay: D(1910, 12, 27), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 21.1, lon: -97.6, prorationGroup: null, recommendedRig: 'rotary',
    leaseBase: 120, leaseYear: 1910,
    boomCurve: [[1910.67, 1], [1911.0, 4], [1912, 3], [1916.1, 6], [1918, 4], [1921, 2], [1922, 0.5], [1925, 0.2]],
    sour: { mult: 0.75, flag: null, multAfter: 0.75 },
    transport: ['teamster', 'gathering_pipe@1910', 'coastal_tanker@1911', 'trunk_pipeline@1912'],
    blurb: 'A chain of buried reef atolls in the Huasteca jungle. Wells here flow a hundred thousand barrels a day, until the salt water comes, and the revolution never quite leaves.',
    strata: stack([
      [30,   'clay',      'Jungle clay',                   2, 8.5, 8],
      [1000, 'shale',     'Méndez shale',                  3, 8.7, 40],
      [1500, 'clay',      'San Felipe marl',               4, 8.8, 40],
      [1750, 'limestone', 'Agua Nueva limestone',          5, 8.8, 150],
      [2700, 'limestone', 'El Abra reef limestone',        6, 8.8, 200, { lossZone: true, frac: 12.4, h2s: 250 }],
      [6000, 'limestone', 'Lower Tamaulipas limestone',    6, 9.4, 200],
    ]),
    prospects: [
      { cx: 1.5, cy: 1.5, rx: 1.4, ry: 1.0, reservoirs: [
        { id: 'gl_casiano', name: 'Juan Casiano atoll', top: 1850, dipFtPerPlot: 60, thick: 150, ooip: 40e6, qiRange: [3000, 40000], pore: 11.6, gusherChance: 0.85,
          ...RES, rf: 0.3, di: 0.3, b: 0.3, api: 21, sour: true, h2s: 300, waterInvasion: { atRemaining: 0.5, rampDays: 45 },
          rivalDrain: [[1910.9, 0], [1912, 0.25], [1916, 0.35], [1919, 0.5], [1921, 0.6], [1923, 0.3]] },
      ] },
      { cx: 4.2, cy: 2, rx: 1.3, ry: 1.0, reservoirs: [
        { id: 'gl_potrero', name: 'Potrero del Llano atoll', top: 1900, dipFtPerPlot: 60, thick: 150, ooip: 80e6, qiRange: [10000, 100000], pore: 11.6, gusherChance: 0.85,
          ...RES, rf: 0.3, di: 0.3, b: 0.3, api: 21, sour: true, h2s: 300, waterInvasion: { atRemaining: 0.5, rampDays: 45 },
          rivalDrain: [[1910.95, 0], [1912, 0.25], [1916, 0.35], [1919, 0.5], [1921, 0.6], [1923, 0.3]] },
      ] },
      { cx: 6.5, cy: 1.5, rx: 1.2, ry: 1.0, reservoirs: [
        { id: 'gl_cerro_azul', name: 'Cerro Azul atoll', top: 1950, dipFtPerPlot: 60, thick: 150, ooip: 100e6, qiRange: [10000, 150000], pore: 11.8, gusherChance: 0.9,
          ...RES, rf: 0.3, di: 0.3, b: 0.3, api: 22, sour: true, h2s: 300, waterInvasion: { atRemaining: 0.5, rampDays: 45 },
          rivalDrain: [[1916.1, 0], [1917, 0.3], [1919, 0.5], [1921, 0.6], [1923, 0.3]] },
      ] },
    ],
    seeps: [9, 12, 14],
    hazards: { lightning: 0.0004, fire: 0.0006, disease: 0.0006 },
  },

  // ======================================================================== Masjed Soleyman, Persia
  {
    id: 'masjed_soleyman', name: 'Masjed Soleyman, Persia', body: 'earth', region: 'persia', regionId: 'persia',
    era: 'gusher', opensDay: D(1908, 1, 1), discoveryDay: D(1908, 5, 26), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 31.93, lon: 49.3, prorationGroup: null, recommendedRig: 'cable',
    leaseBase: 150, leaseYear: 1908, royalty: 0.16, // D'Arcy concession: 16% of net profits to the Shah
    boomCurve: [[1908.4, 1], [1909.3, 3], [1912, 2], [1914, 2.5]],
    sour: { mult: 0.9, flag: null, multAfter: 0.9 },
    transport: ['teamster', 'trunk_pipeline@1911', 'coastal_tanker@1912'],
    blurb: 'Seven years of dry holes, and the money was nearly gone. Then Reynolds’ well blew oil over the derrick in the Bakhtiari hills. Mind the gas in the Fars beds.',
    strata: stack([
      [60,   'gravel',    'Bakhtiari scree',               5, 8.5, 5],
      [450,  'gravel',    'Bakhtiari conglomerate',        6, 8.6, 60],
      [900,  'anhydrite', 'Lower Fars gypsum, salt & marl', 4, 12.2, 15, { gas: true, salt: true, h2s: 200 }],
      [1700, 'limestone', 'Asmari limestone (fractured)',  6, 8.8, 200, { h2s: 600, lossZone: true, frac: 12.8 }],
      [6000, 'limestone', 'Pabdeh & Bangestan',            6, 9.4, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 3.2, ry: 1.3, reservoirs: [
        { id: 'ms_asmari', name: 'Asmari limestone', top: 1000, dipFtPerPlot: 40, thick: 250, ooip: 300e6, qiRange: [1000, 30000], pore: 10.4, gusherChance: 0.6,
          ...RES, rf: 0.25, di: 0.12, api: 37, sour: true, h2s: 600 },
      ] },
    ],
    seeps: [8, 9, 15],
    hazards: { lightning: 0.0002, fire: 0.0004, disease: 0.0004, heat: 0.0003 },
  },

  // ======================================================================== Maracaibo (La Rosa), Venezuela
  {
    id: 'maracaibo', name: 'La Rosa, Lake Maracaibo', body: 'earth', region: 'venezuela', regionId: 'venezuela',
    era: 'gusher', opensDay: D(1922, 1, 1), discoveryDay: D(1922, 12, 14), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 10.4, lon: -71.45, prorationGroup: null, recommendedRig: 'rotary',
    leaseBase: 200, leaseYear: 1922,
    boomCurve: [[1922.0, 1], [1922.95, 6], [1923.5, 5], [1925, 4], [1929, 3], [1932, 1.5]],
    transport: ['teamster', 'gathering_pipe@1922', 'coastal_tanker@1922'],
    blurb: 'Shell’s Barroso No. 2 at Cabimas blew out at 1,500 ft and drenched the town for nine days. Concessions come from General Gómez, and his friends.',
    strata: stack([
      [50,   'clay',      'Lake clay',                     2, 8.5, 5],
      [300,  'sand',      'El Milagro heaving sand',       1, 8.8, 1, HEAVE],
      [1100, 'clay',      'La Puerta clays',               2, 8.7, 10, GUMBO],
      [1350, 'sand',      'Lagunillas sands',              3, 8.8, 15],
      [1500, 'shale',     'La Rosa shale',                 3, 8.8, 25],
      [2400, 'sand',      'Santa Bárbara (La Rosa) sands', 3, 8.8, 15],
      [6000, 'shale',     'Misoa & Eocene',                5, 9.5, 60],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.6, ry: 1.6, reservoirs: [
        { id: 'mc_lagunillas', name: 'Lagunillas sands', top: 1120, dipFtPerPlot: 25, thick: 120, ooip: 150e6, qiRange: [200, 5000], pore: 9.6, gusherChance: 0.25,
          ...RES, di: 0.35, api: 18 },
        { id: 'mc_la_rosa', name: 'La Rosa (Barroso) sands', top: 1540, dipFtPerPlot: 35, thick: 200, ooip: 200e6, qiRange: [5000, 100000], pore: 11.2, gusherChance: 0.7,
          ...RES, rf: 0.2, di: 0.4, api: 24, rivalDrain: [[1923, 0], [1925, 0.15], [1930, 0.2], [1940, 0.1]] },
      ] },
      { cx: 6, cy: 2, rx: 1.6, ry: 1.4, reservoirs: [
        { id: 'mc_east', name: 'Tía Juana sands', top: 1600, dipFtPerPlot: 35, thick: 150, ooip: 80e6, qiRange: [1000, 20000], pore: 10.6, gusherChance: 0.5, ...RES, di: 0.4, api: 22 },
      ] },
    ],
    seeps: [9, 10, 13],
    hazards: { lightning: 0.0004, fire: 0.0005, disease: 0.0005 },
  },

  // ======================================================================== Kirkuk (Baba Gurgur), Iraq
  {
    id: 'kirkuk', name: 'Baba Gurgur, Kirkuk', body: 'earth', region: 'iraq', regionId: 'iraq',
    era: 'gusher', opensDay: D(1927, 6, 1), discoveryDay: D(1927, 10, 14), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 35.5, lon: 44.4, prorationGroup: null, recommendedRig: 'rotary',
    leaseBase: 150, leaseYear: 1927,
    boomCurve: [[1927.5, 1], [1927.8, 3], [1931, 2], [1934.5, 4], [1940, 3]],
    sour: { mult: 0.85, flag: null, multAfter: 0.85 },
    // No pipeline to the Mediterranean until 1934: until then crude must go by rail to Basra.
    transport: ['teamster', 'rail@1927', 'trunk_pipeline@1934'],
    blurb: 'The eternal fires of Baba Gurgur burn over a long limestone ridge. Under them: a giant, and gas that kills.',
    strata: stack([
      [100,  'gravel',    'Bakhtiari gravels',             5, 8.5, 5],
      [600,  'clay',      'Upper Fars marl',               3, 8.7, 30],
      [1050, 'anhydrite', 'Lower Fars anhydrite & salt',   4, 11.8, 15, { gas: true, salt: true, h2s: 800 }],
      [2600, 'limestone', 'Main limestone (Kirkuk group)', 6, 8.8, 200, { lossZone: true, frac: 12.6, h2s: 2000 }],
      [6000, 'limestone', 'Shiranish & deeper',            6, 9.5, 200],
    ]),
    prospects: [
      { cx: 1.5, cy: 1.5, rx: 1.8, ry: 1.0, reservoirs: [
        { id: 'ki_baba', name: 'Baba dome', top: 1100, dipFtPerPlot: 60, thick: 400, ooip: 400e6, qiRange: [5000, 95000], pore: 10.8, gusherChance: 0.8,
          ...RES, rf: 0.3, di: 0.08, api: 36, sour: true, h2s: 2000 },
      ] },
      { cx: 4.0, cy: 1.5, rx: 1.4, ry: 0.9, reservoirs: [
        { id: 'ki_avanah', name: 'Avanah dome', top: 1300, dipFtPerPlot: 60, thick: 350, ooip: 150e6, qiRange: [3000, 50000], pore: 10.6, gusherChance: 0.7,
          ...RES, rf: 0.3, di: 0.08, api: 36, sour: true, h2s: 1500 },
      ] },
      { cx: 6.5, cy: 2, rx: 1.2, ry: 0.9, reservoirs: [
        { id: 'ki_khurmala', name: 'Khurmala dome', top: 1500, dipFtPerPlot: 60, thick: 300, ooip: 60e6, qiRange: [1000, 20000], pore: 10.4, gusherChance: 0.5,
          ...RES, rf: 0.3, di: 0.1, api: 35, sour: true, h2s: 1500 },
      ] },
    ],
    seeps: [9, 10, 12],
    hazards: { lightning: 0.0002, fire: 0.0008, heat: 0.0003 },
  },

  // ======================================================================== Bahrain (Awali)
  {
    id: 'bahrain', name: 'Jebel Dukhan, Bahrain', body: 'earth', region: 'bahrain', regionId: 'bahrain',
    era: 'gusher', opensDay: D(1931, 10, 16), discoveryDay: D(1932, 6, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 26.03, lon: 50.55, prorationGroup: null, recommendedRig: 'rotary',
    leaseBase: 120, leaseYear: 1931,
    boomCurve: [[1931.8, 1], [1932.45, 3], [1934, 2]],
    transport: ['gathering_pipe@1932', 'coastal_tanker@1934'],
    blurb: 'A lone dome under the Mountain of Smoke. Fred Davies bet on it; Socal paid for it. The first oil on the Arab shore of the Gulf.',
    strata: stack([
      [60,   'sand',      'Coastal sand & sabkha',         1, 8.5, 3],
      [550,  'limestone', 'Dammam & Rus limestone',        5, 8.6, 150],
      [700,  'limestone', 'Umm er Radhuma artesian aquifer', 5, 8.9, 150, WATER],
      [1900, 'shale',     'Aruma & Wasia shales',          3, 8.8, 40],
      [2400, 'limestone', 'Mauddud limestone',             5, 8.8, 150],
      [6000, 'limestone', 'Arab zone & deep gas',          6, 9.6, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.0, ry: 1.5, reservoirs: [
        { id: 'bh_mauddud', name: 'Mauddud limestone', top: 2000, dipFtPerPlot: 30, thick: 150, ooip: 80e6, qiRange: [400, 6000], pore: 9.6, gusherChance: 0.2,
          ...RES, rf: 0.25, di: 0.15, api: 33, h2s: 60 },
      ] },
    ],
    seeps: [8, 9],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0004 },
  },

  // ======================================================================== Dammam, Saudi Arabia (Era 3 teaser)
  {
    id: 'dammam', name: 'Dammam Dome, al-Hasa', body: 'earth', region: 'saudi', regionId: 'saudi',
    era: 'gusher', opensDay: D(1935, 4, 30), discoveryDay: D(1938, 3, 3), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 26.32, lon: 50.13, prorationGroup: null, recommendedRig: 'rotary',
    leaseBase: 100, leaseYear: 1935, royalty: 0.12, // 4 gold shillings a ton under the 1933 Socal concession
    boomCurve: [[1935.3, 1], [1938.17, 4], [1940, 3]],
    sour: { mult: 0.92, flag: null, multAfter: 0.92 },
    transport: ['teamster', 'gathering_pipe@1938', 'coastal_tanker@1939'], // early crude went by truck and barge to the Bahrain refinery
    blurb: 'Six wells, six disappointments in the shallow Bahrain zone. Max Steineke says go deeper. Well No. 7 is still drilling.',
    strata: stack([
      [50,   'sand',      'Dune sand',                     1, 8.5, 3],
      [600,  'limestone', 'Dammam & Rus limestone',        5, 8.6, 150],
      [800,  'limestone', 'Umm er Radhuma aquifer',        5, 8.9, 150, WATER],
      [2200, 'shale',     'Aruma & Wasia shale',           3, 8.8, 40],
      [3300, 'sand',      'Biyadh & Wasia sands',          4, 8.8, 25],
      [4600, 'limestone', 'Thamama & Buwaib limestone',    6, 8.8, 200],
      [5300, 'limestone', 'Arab Zone limestone',           6, 8.8, 200],
      [7500, 'limestone', 'Hanifa & Tuwaiq',               6, 9.4, 200],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.2, ry: 1.6, reservoirs: [
        { id: 'dm_shallow', name: 'Bahrain zone (shallow)', top: 2300, dipFtPerPlot: 25, thick: 60, ooip: 3e6, qiRange: [10, 120], pore: 9.2, gusherChance: 0, ...RES, rf: 0.15, di: 0.6, api: 30 },
        { id: 'dm_arab', name: 'Arab Zone', top: 4700, dipFtPerPlot: 40, thick: 200, ooip: 300e6, qiRange: [1000, 8000], pore: 9.8, gusherChance: 0.25,
          ...RES, rf: 0.3, di: 0.08, api: 34, sour: true, h2s: 400 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0005 },
  },

  // ======================================================================== Turner Valley, Alberta
  {
    id: 'turner_valley', name: 'Turner Valley, Alberta', body: 'earth', region: 'alberta', regionId: 'alberta',
    era: 'gusher', opensDay: D(1914, 5, 14), discoveryDay: D(1914, 5, 14), grid: { w: 8, h: 3 }, waterDepthFt: 0,
    lat: 50.67, lon: -114.28, prorationGroup: null, recommendedRig: 'rotary', flareGas: true,
    leaseBase: 200, leaseYear: 1914,
    boomCurve: [[1914.37, 1], [1914.5, 12], [1914.8, 4], [1916, 0.5], [1924.8, 2], [1926, 1], [1936.45, 3], [1938, 2]],
    transport: ['teamster', 'rail@1914', 'gathering_pipe@1914'],
    blurb: 'Dingman’s naphtha set off a Calgary stock frenzy in 1914. Royalite No. 4 burned for weeks in 1924. The real oil leg lies deep on the west flank, and nobody has found it yet.',
    strata: stack([
      [60,   'gravel',    'Glacial gravel',                1, 8.5, 3],
      [1800, 'shale',     'Belly River & Alberta shale',   3, 8.7, 50],
      [2900, 'sand',      'Blairmore sandstone',           5, 8.8, 60],
      [3550, 'shale',     'Fernie shale',                  3, 8.8, 40],
      [7800, 'limestone', 'Rundle (Turner Valley) limestone', 6, 8.8, 200, { h2s: 3000 }],
    ]),
    prospects: [
      { cx: 3.5, cy: 1, rx: 3.6, ry: 0.9, reservoirs: [
        { id: 'tv_dingman', name: 'Dingman naphtha sand', top: 2700, dipFtPerPlot: 30, thick: 80, ooip: 2e6, qiRange: [20, 200], pore: 9.4, gusherChance: 0.05,
          ...RES, rf: 0.3, di: 0.8, api: 70, h2s: 500, condensate: true },
        { id: 'tv_royalite', name: 'Turner Valley gas cap', top: 3650, dipFtPerPlot: 60, thick: 300, ooip: 20e6, qiRange: [100, 1000], pore: 10.2, gusherChance: 0.4,
          ...RES, rf: 0.3, di: 0.5, api: 65, sour: true, h2s: 8000, condensate: true, gasCap: true },
      ] },
      { cx: 3.5, cy: 2.2, rx: 3.4, ry: 0.8, reservoirs: [
        { id: 'tv_oil_leg', name: 'Turner Valley oil leg', top: 6500, dipFtPerPlot: 80, thick: 300, ooip: 100e6, qiRange: [300, 1500], pore: 9.8, gusherChance: 0.1,
          ...RES, rf: 0.15, di: 0.2, api: 40, sour: true, h2s: 3000 },
      ] },
    ],
    seeps: [10, 11],
    hazards: { lightning: 0.0002, fire: 0.0006, cold: 0.0003 },
  },
];
